<template>
    <article
        v-for="(showcase, showcaseIndex) in showcases"
        :key="showcase.uid"
        class="pa-6 section-border-y"
    >
        <v-row no-gutters>
            <v-col>
                <h2
                    class="text-center text-uppercase font-size-5 letter-spacing-5 font-weight-bold py-0 my-0"
                >
                    {{ showcase.heading }}
                </h2>
            </v-col>
        </v-row>
        <ContainerContent>
            <template
                v-if="!isMobile && showcase.blocks.length > 3"
            >
                <v-carousel
                    v-model="carouselModels[showcaseIndex]"
                    color="secondary"
                    height="auto"
                    hide-delimiters
                    hide-delimiter-background
                >
                    <template #prev="{ props: slotProps }">
                        <SliderButton
                            type="prev"
                            alt="Previous Slide"
                            :opacity="0.8"
                            v-bind="slotProps"
                        ></SliderButton>
                    </template>
                    <template #next="{ props: slotProps }">
                        <SliderButton
                            type="next"
                            alt="Next Slide"
                            :opacity="0.8"
                            v-bind="slotProps"
                        ></SliderButton>
                    </template>
                    <v-carousel-item
                        v-for="(blockChunk, index) in lo_chunk(showcase.blocks, 3)"
                        :key="index"
                    >
                        <v-row>
                            <v-col
                                v-for="block in blockChunk"
                                :key="block.uid"
                                cols="12"
                                md="4"
                            >
                                <BlockRenderer
                                    :model-value="block"
                                    full-bleed
                                ></BlockRenderer>
                            </v-col>
                        </v-row>
                    </v-carousel-item>
                </v-carousel>
            </template>
            <v-row
                v-else
            >
                <v-col
                    v-for="block in showcase.blocks"
                    :key="block.uid"
                    cols="12"
                    md="4"
                >
                    <BlockRenderer
                        :model-value="block"
                        full-bleed
                    ></BlockRenderer>
                </v-col>
            </v-row>
        </ContainerContent>
    </article>
</template>

<script lang="ts" setup>
    import type {
        Showcase,
        ShowcaseGroup
    } from '~/types';
    import ContainerContent from '~/components/ContainerContent/ContainerContent.vue';
    import SliderButton from '~/components/carousel/SliderButton/SliderButton.vue';
    import BlockRenderer from '~/components/content/blocks/v2/BlockRenderer/BlockRenderer.vue';
    import {
        useContentBlocks,
        useIsMobile
    } from '~/composables';
    import { chunk as lo_chunk } from 'es-toolkit/compat';

    const { isMobile } = useIsMobile();
    const {
        getPreviewToken,
        getContentBlocksByPlacement
    } = useContentBlocks();

    const previewToken = getPreviewToken('showcase-group');
    const showcaseGroupOptions = {
        type: 'showcase-groups',
        uid: 'shgrp_0VTQdRVyGqtACRdprULr'
    };

    const { data } = await useAsyncData(
        'featured-products-content-blocks',
        () => getContentBlocksByPlacement<ShowcaseGroup>(
            showcaseGroupOptions,
            previewToken
        )
    );

    // Skip payloads BlockRenderer cannot bind (missing/unknown template ref)
    // and showcases left with no renderable tiles.
    const showcases = computed<Showcase[]>(() =>
        (data.value?.showcases ?? [])
            .map((showcase) => ({
                ...showcase,
                blocks: showcase.blocks.filter((block) => !!block.template?.ref)
            }))
            .filter((showcase) => showcase.blocks.length > 0)
    );

    const carouselModels = ref<number[]>([]);
</script>

<style lang="scss"></style>
